import { ROUTES, documentsUpload, isCustomerDocumentFilterSlug } from "./routes";
import { TOUCHPOINTS, type TouchpointName } from "./touchpoints";

/**
 * Who is asking. The same hash resolves to different touchpoints per side — a customer has ONE
 * mandate, so `#/dokumente` and `#/aufgaben` resolve flat against it, while a trustee gets the
 * cross-mandate overviews. Without this the table would silently serve trustee views to customers.
 */
export interface RouteContext {
    side: "trexpert" | "customer";
    /**
     * The customer's single mandate, or the trustee's settings mandate. Customer routes are
     * meaningless without it; trustee routes carry their ids in the hash instead.
     */
    mandateId?: string;
}

/** What a hash route resolves to: a touchpoint plus the properties it needs to show the right thing. */
export interface RouteTarget {
    touchpoint: TouchpointName;
    properties?: Record<string, string>;
}

/**
 * Where a hash route leads, as touchpoint + properties.
 *
 * ONE table, two consumers. The standalone shell mounts the result directly; hosted in app-base the
 * client turns it into a `/navigation/goto` request so the HOST can route (app-base routes by path —
 * a hash link there changes the fragment and nothing else, which is why every in-app link was inert).
 *
 * Extracted from the shell's own routing rather than mirrored beside it: two copies of this table
 * would drift, and the failure mode is silent — a link that quietly stops navigating, which is
 * exactly the bug this exists to fix.
 *
 * Returns undefined for a hash nothing owns, so a caller leaves the link alone rather than
 * swallowing the click.
 */
export const routeToTarget = (hash: string, context: RouteContext): RouteTarget | undefined =>
    context.side === "customer"
        ? customerTarget(hash, context.mandateId)
        : trusteeTarget(hash, context.mandateId);

/**
 * Customer-flat routing (Epic A): one Mandate per customer, so Dateien, Aufgaben and Belege
 * hochladen resolve against that single `mandateId` without an id in the URL.
 *
 * `#/inbox` is deliberately absent: the customer inbox is hidden, and standalone redirects it to the
 * dashboard. A redirect is the shell's business, not a destination, so this reports "not mine" and
 * the caller leaves the link alone.
 */
const customerTarget = (hash: string, mandateId?: string): RouteTarget | undefined => {
    if (mandateId === undefined) {
        return undefined;
    }
    const documentFilter = /^#\/dokumente\/([^/]+)$/.exec(hash)?.[1];
    if (documentFilter !== undefined) {
        return {
            touchpoint: TOUCHPOINTS.mandateWorkspace,
            properties: {
                mandateId,
                ...(isCustomerDocumentFilterSlug(documentFilter)
                    ? { initialDocumentFilter: documentFilter }
                    : {}),
            },
        };
    }
    if (hash === ROUTES.dokumente) {
        return { touchpoint: TOUCHPOINTS.mandateWorkspace, properties: { mandateId } };
    }
    const taskListId = /^#\/aufgaben\/([^/]+)$/.exec(hash)?.[1];
    if (taskListId !== undefined) {
        return { touchpoint: TOUCHPOINTS.taskListView, properties: { mandateId, taskListId } };
    }
    if (hash === ROUTES.aufgaben) {
        return { touchpoint: TOUCHPOINTS.taskListView, properties: { mandateId } };
    }
    if (hash === ROUTES.hochladen) {
        return { touchpoint: TOUCHPOINTS.uploadDocument, properties: { mandateId } };
    }
    if (hash === ROUTES.settings) {
        return { touchpoint: TOUCHPOINTS.settings, properties: { mandateId } };
    }
    if (hash === ROUTES.formulare) {
        // The external-form link list is global — no mandate scoping (S11).
        return { touchpoint: TOUCHPOINTS.forms };
    }
    return undefined;
};

/**
 * Trustee routing: mandate-scoped routes plus the flat Dashboard / Dokumente / Aufgaben /
 * Posteingang / Einstellungen.
 *
 * Order matters and mirrors the shell: the mandate-less upload (`#/dokumente/hochladen`) is matched
 * BEFORE the status deep-link, or `hochladen` would be read as a status filter.
 */
const trusteeTarget = (hash: string, settingsMandateId?: string): RouteTarget | undefined => {
    const uploadMandateId = /^#\/mandates\/(.+)\/upload$/.exec(hash)?.[1];
    if (uploadMandateId !== undefined) {
        return { touchpoint: TOUCHPOINTS.uploadDocument, properties: { mandateId: uploadMandateId } };
    }
    const taskListDetail = /^#\/mandates\/([^/]+)\/task-lists\/([^/]+)$/.exec(hash);
    if (taskListDetail?.[1] !== undefined && taskListDetail[2] !== undefined) {
        return {
            touchpoint: TOUCHPOINTS.taskListDetail,
            properties: { mandateId: taskListDetail[1], taskListId: taskListDetail[2] },
        };
    }
    const taskListsMandateId = /^#\/mandates\/([^/]+)\/task-lists$/.exec(hash)?.[1];
    if (taskListsMandateId !== undefined) {
        return { touchpoint: TOUCHPOINTS.taskListEditor, properties: { mandateId: taskListsMandateId } };
    }
    const taskPlansMandateId = /^#\/mandates\/([^/]+)\/task-plans$/.exec(hash)?.[1];
    if (taskPlansMandateId !== undefined) {
        return { touchpoint: TOUCHPOINTS.taskPlans, properties: { mandateId: taskPlansMandateId } };
    }
    if (hash === documentsUpload()) {
        return { touchpoint: TOUCHPOINTS.uploadDocument };
    }
    const initialStatus = /^#\/dokumente\/([^/]+)$/.exec(hash)?.[1];
    if (initialStatus !== undefined) {
        return { touchpoint: TOUCHPOINTS.documentOverview, properties: { initialStatus } };
    }
    if (hash === ROUTES.dokumente) {
        return { touchpoint: TOUCHPOINTS.documentOverview };
    }
    if (hash === ROUTES.aufgaben) {
        return { touchpoint: TOUCHPOINTS.taskOverview };
    }
    if (hash === ROUTES.inbox) {
        return { touchpoint: TOUCHPOINTS.inbox };
    }
    if (hash === ROUTES.settings) {
        return {
            touchpoint: TOUCHPOINTS.settings,
            ...(settingsMandateId === undefined ? {} : { properties: { mandateId: settingsMandateId } }),
        };
    }
    if (hash === ROUTES.formulare) {
        return { touchpoint: TOUCHPOINTS.forms };
    }
    if (hash === ROUTES.dashboard) {
        return { touchpoint: TOUCHPOINTS.dashboard };
    }
    if (hash === ROUTES.mandates) {
        return { touchpoint: TOUCHPOINTS.mandateList };
    }
    // The standalone shell REDIRECTS this one so the URL is corrected; either way the destination
    // is the workspace, and a caller that cannot rewrite the URL still lands in the right place.
    const settingsMandate = /^#\/mandates\/([^/]+)\/settings$/.exec(hash)?.[1];
    if (settingsMandate !== undefined) {
        return { touchpoint: TOUCHPOINTS.mandateWorkspace, properties: { mandateId: settingsMandate } };
    }
    // A mandate id is matched LAST: every `#/mandates/<id>/…` sub-route above must win first.
    const mandateId = /^#\/mandates\/([^/]+)$/.exec(hash)?.[1];
    if (mandateId !== undefined) {
        return { touchpoint: TOUCHPOINTS.mandateWorkspace, properties: { mandateId } };
    }
    // Deliberately NOT the shell's "default landing is the dashboard": that is a shell decision for
    // an unrecognised URL. Here, unknown means "not a route we own", so a caller leaves the link be
    // rather than sending every stray anchor to the dashboard.
    return undefined;
};
